import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { StatisticType, StatisticUnit } from '@/types';
import {
    SummaryViewPropertyBase,
    StatusSummaryDataAPI,
    SUPPORTED_STATISTIC_TYPES,
    SummaryViewTableRow,
    TableMetric,
    SummaryViewRenderContext,
} from '@/types/summaryView';
import {
    isMultiInstanceArchetypeProperty,
    getSummaryViewPropertyLabel,
    isArchetypeProperty,
    isStatisticProperty,
    getStatisticFromProperty,
} from '@/helpers/summaryViewHelpers';
import {
    StatusCell,
    NoBuildsCellComponent,
    TableDateCell,
    DurationCell,
    EmptyCell,
    HealthMetricCell,
    getBackgroundColorForHealthMetric,
    ReleaseStepCell,
    lightBackgroundColorForStatus,
    shouldShowStepEmptyState,
} from './OverviewTableCellComponents';
import { ReleasePilotWidget } from '@/components/appHome/widgets/ReleasePilotWidget';
import { FeatureCompletionWidget } from '@/components/appHome/widgets/FeatureCompletionWidget';
import { PhasedReleaseChartBase, AdoptionRateWidget } from '@/components/appHome/widgets/LiveReleaseChartWidget';
import AppStoreBuildComponent from '@/components/appStoreBuilds/AppStoreBuildComponent';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { displayStatValue } from '@/helpers';
import { antGrey } from '@/components/styles/globalStyles';
import Space from 'antd/lib/space';
import { dayjs } from '@/extendedDayjs';
import { displayReleaseOverviewStatisticLabel } from '@/components/StatCard';

const createMetric = (
    key: string,
    renderContent: (data: any, context: SummaryViewRenderContext) => React.ReactNode,
    customTitle?: string,
): TableMetric => ({
    key,
    title: customTitle || getSummaryViewPropertyLabel(key),
    renderCell: (context) => {
        const data = context.row[key];
        if (!data && data !== 0) return <EmptyCell />;
        return renderContent(data, context);
    },
});

const createDateMetric = (key: string): TableMetric =>
    createMetric(key, (data, { currentAppId }) => <TableDateCell date={data} appId={currentAppId} />);

const createArchetypeMetric = (instanceKey: string, propertyKey: string): TableMetric => ({
    key: instanceKey,
    title: getSummaryViewPropertyLabel(propertyKey),
    renderCell: ({ row }) => {
        const stepData = row[instanceKey];
        return <ReleaseStepCell stepData={stepData} />;
    },
    getCellStyle: (row) => {
        const stepData = row[instanceKey];
        if (stepData && stepData.status && !shouldShowStepEmptyState(stepData)) {
            return { backgroundColor: lightBackgroundColorForStatus(stepData.status) };
        }
        return {};
    },
});

const createStatisticMetric = (propertyKey: string, statisticType: StatisticType, sampleStat: any): TableMetric => {
    const isDuration =
        statisticType === StatisticType.WaitingForReviewDuration || statisticType === StatisticType.InReviewDuration;
    const statTitle = displayReleaseOverviewStatisticLabel(statisticType, sampleStat?.additionalData);

    return {
        key: propertyKey,
        title: statTitle || displayReleaseOverviewStatisticLabel(statisticType),
        renderCell: ({ row }) => {
            const data = row[propertyKey];
            if (!data || data.value === undefined) return <EmptyCell />;

            if (isDuration) {
                const durationText = dayjs.duration(data.value, 'seconds').humanize();
                return <DurationCell durationText={durationText} />;
            }

            const formattedValue = displayStatValue(statisticType, data.value, data.unit);
            return (
                <HealthMetricCell
                    value={formattedValue.toString()}
                    change={data.change}
                    changeStatus={data.changeStatus}
                />
            );
        },
        getCellStyle: (row) => {
            if (isDuration) return {};
            const data = row[propertyKey];
            if (data && data.changeStatus) {
                return { backgroundColor: getBackgroundColorForHealthMetric(data.changeStatus) };
            }
            return {};
        },
    };
};

export const createSummaryViewMetrics = (
    selectedPropertyKeys: string[],
    rows: SummaryViewTableRow[] = [],
): TableMetric[] => {
    const baseMetrics: TableMetric[] = [
        {
            key: SummaryViewPropertyBase.WorkflowRunVersion,
            title: getSummaryViewPropertyLabel(SummaryViewPropertyBase.WorkflowRunVersion),
            renderCell: ({ row }) => <Text code>{row.version}</Text>,
        },
        createMetric(SummaryViewPropertyBase.Status, (data: StatusSummaryDataAPI, { row, compact }) => {
            const rolloutArchetype = row['Archetype:rollout'];
            return (
                <StatusCell
                    type={data.type}
                    status={data.status}
                    timelinePhase={data.timelinePhase}
                    phasedReleaseInfo={data.phasedReleaseInfo}
                    rolloutExtraData={rolloutArchetype?.extraData}
                    compact={compact}
                />
            );
        }),
        createMetric(SummaryViewPropertyBase.Builds, (builds) => {
            if (!builds || !Array.isArray(builds) || builds.length === 0) {
                return <NoBuildsCellComponent />;
            }

            const visibleBuilds = builds.slice(0, 2);
            const remainingCount = builds.length - visibleBuilds.length;

            return (
                <Space size={4} wrap={false}>
                    {visibleBuilds.map((build) => (
                        <AppStoreBuildComponent
                            key={build?.appStoreBuild?.id ?? build?.appStoreBuild?.buildNumber}
                            {...build}
                        />
                    ))}
                    {remainingCount > 0 && (
                        <Text style={{ color: antGrey.grey6, fontSize: 12 }}>+{remainingCount} more</Text>
                    )}
                </Space>
            );
        }),
        {
            key: SummaryViewPropertyBase.ReleasePilot,
            title: getSummaryViewPropertyLabel(SummaryViewPropertyBase.ReleasePilot),
            renderCell: (context) => {
                const data = context.row[SummaryViewPropertyBase.ReleasePilot];
                const destination = context.appsById[context.row.destinationId];
                const appId = destination?.appId || context.currentAppId;
                return <ReleasePilotWidget appId={appId} releasePilotId={data} showTitle={false} />;
            },
        },
        createMetric(SummaryViewPropertyBase.FeatureCompletePercent, (data) => {
            if (!data) return <EmptyCell />;
            return <FeatureCompletionWidget featuresStep={data} compact />;
        }),
        createDateMetric(SummaryViewPropertyBase.TargetKickoffDates),
        createDateMetric(SummaryViewPropertyBase.TargetSubmitDates),
        createDateMetric(SummaryViewPropertyBase.TargetReleaseDates),
        createDateMetric(SummaryViewPropertyBase.KickedOffDate),
        createDateMetric(SummaryViewPropertyBase.SubmittedDate),
        createDateMetric(SummaryViewPropertyBase.ReleasedAtDate),
        createMetric(SummaryViewPropertyBase.PhasedRelease, (phasedRelease) => {
            if (!phasedRelease) return <EmptyCell />;

            return (
                <PhasedReleaseChartBase
                    percentage={phasedRelease.phasedReleasePercent ?? 0}
                    dayNumber={phasedRelease.dayNumber ?? 0}
                    daysToPercent={phasedRelease.daysToPercentSchedule}
                    isAutomatedPhasedRollout={phasedRelease.isAutomatedPhasedRollout ?? false}
                    wasHalted={phasedRelease.isPaused ?? false}
                    wasAccelerated={phasedRelease.isAccelerated ?? false}
                    compact={true}
                />
            );
        }),
        createMetric(SummaryViewPropertyBase.AdoptionRate, (builds) => {
            if (!builds || !Array.isArray(builds) || builds.length === 0) return <EmptyCell />;

            const selectedBuild = builds.find((build) => build?.stabilityInfo?.sessionAdoption != null) ?? builds[0];
            const sessionAdoption = selectedBuild?.stabilityInfo?.sessionAdoption;

            if (!sessionAdoption) return <EmptyCell />;

            const integrationId = selectedBuild?.stabilityInfo?.integrationId;
            const [stabIcon, stabAlt] = iconAndAltForIntegrationType(integrationId);

            return (
                <AdoptionRateWidget sessionAdoption={sessionAdoption} stabIcon={stabIcon} stabAlt={stabAlt} compact />
            );
        }),
        createMetric(SummaryViewPropertyBase.CrashFreeUsers, (value) => {
            const formattedValue = displayStatValue(StatisticType.CrashFreeUsers, value, StatisticUnit.Percent);
            return <HealthMetricCell value={formattedValue.toString()} />;
        }),
        createMetric(SummaryViewPropertyBase.CrashFreeSessions, (value) => {
            const formattedValue = displayStatValue(StatisticType.CrashFreeSessions, value, StatisticUnit.Percent);
            return <HealthMetricCell value={formattedValue.toString()} />;
        }),
    ];

    const allMetrics: TableMetric[] = [...baseMetrics];

    selectedPropertyKeys.forEach((propertyKey) => {
        if (isArchetypeProperty(propertyKey)) {
            const useInstances = isMultiInstanceArchetypeProperty(propertyKey);
            const instanceKeys: string[] = [];

            if (useInstances) {
                const uniqueKeys = new Set<string>();
                rows.forEach((row) => {
                    Object.keys(row).forEach((key) => {
                        if (key.startsWith(`${propertyKey}:`)) {
                            uniqueKeys.add(key);
                        }
                    });
                });

                instanceKeys.push(...uniqueKeys);
            }

            if (!useInstances || instanceKeys.length === 0) {
                instanceKeys.push(propertyKey);
            }

            instanceKeys.forEach((instanceKey) => {
                allMetrics.push(createArchetypeMetric(instanceKey, propertyKey));
            });
        }

        if (isStatisticProperty(propertyKey)) {
            const statisticType = getStatisticFromProperty(propertyKey) as StatisticType | null;
            if (!statisticType || !SUPPORTED_STATISTIC_TYPES.includes(statisticType)) {
                return;
            }
            const sampleStat = rows.find((row) => row[propertyKey])?.[propertyKey];
            allMetrics.push(createStatisticMetric(propertyKey, statisticType, sampleStat));
        }
    });

    const alwaysVisibleMetrics = [SummaryViewPropertyBase.WorkflowRunVersion];

    return allMetrics.filter((metric) => {
        if (alwaysVisibleMetrics.includes(metric.key as SummaryViewPropertyBase)) return true;
        if (selectedPropertyKeys.includes(metric.key)) return true;
        return selectedPropertyKeys.some((selectedKey) => metric.key.startsWith(`${selectedKey}:`));
    });
};
